import {useEffect, useState} from 'react';
import {Controller, FieldValues, Path, SubmitHandler, useForm, useWatch} from 'react-hook-form';

import {FormFieldT} from '@/lib/types/form.types';

import {formatPhoneNumber} from '@/lib/helpers/formatPhoneNumber';

import {Map} from '@/components/map/map';

interface FormModalProps<T extends FieldValues> {
  isVisible: boolean;
  onClose?: () => void;
  error?: string | null;
  onSubmit: SubmitHandler<T>;
  fields: FormFieldT<T>[];
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formData: any;
  fieldsPerPage?: number;
  isSubmitting?: boolean;
  setFormData?: (data: T) => void;
}

const MAX_PHONE_LENGTH = 15; // Adjust this based on your specific format and use case

export const ModalFormComponent = <T extends FieldValues>({
  isVisible,
  onClose,
  onSubmit,
  fields,
  title,
  formData,
  fieldsPerPage = 6,
  error,
  isSubmitting,
  setFormData,
}: FormModalProps<T>) => {
  const {
    control,
    handleSubmit,
    formState: {errors},
    reset,
  } = useForm<T>({
    defaultValues: formData,
  });

  const watchedFields = useWatch<T>({control});

  useEffect(() => {
    if (isVisible) {
      reset(formData);
    }
  }, [formData, isVisible, reset]);

  useEffect(() => {
    if (setFormData && isVisible) {
      // Debounce the setFormData call to prevent infinite loops
      const timeoutId = setTimeout(() => {
        setFormData(watchedFields as T);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [watchedFields, setFormData, isVisible]);

  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = Math.ceil(fields.length / fieldsPerPage);

  const currentFields = fields.slice(currentPage * fieldsPerPage, (currentPage + 1) * fieldsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleClose = () => {
    setCurrentPage(0);
    onClose?.();
  };

  return (
    <div
      className={`hs-overlay ${isVisible ? 'flex' : 'hidden'} ti-modal fixed inset-0 items-center justify-center bg-black bg-opacity-30`}
    >
      <div className="ti-modal-box modal-dialog-centered mt-0 max-h-[80vh] w-full overflow-hidden !opacity-100 ease-out hs-overlay-open:mt-7 sm:w-1/2 md:w-1/3 lg:w-1/4">
        <div className="ti-modal-content flex h-full flex-col rounded-lg bg-white shadow-lg">
          <div className="ti-modal-header flex-shrink-0 border-b p-4">
            <h6 className="modal-title !text-default text-[1rem] font-semibold dark:text-defaulttextcolor/70">
              {title}
            </h6>
            <button
              type="button"
              className="hs-dropdown-toggle !text-[1rem] !font-semibold"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              <span className="sr-only">Close</span>
              <i className="ri-close-line"></i>
            </button>
          </div>
          {error && (
            <div className="alert-danger mb-2 px-4 py-3 shadow-md" role="alert">
              <div className="flex">
                <div className="py-1"></div>
                <div>{error}</div>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className="flex-grow overflow-y-auto">
            <div className="ti-modal-body px-6 py-4">
              <div className="gy-2 grid grid-cols-12">
                {currentFields.map((field, index) => (
                  <div key={`${String(field.name)}-${index}`} className="col-span-12 mb-2 xl:col-span-12">
                    <label htmlFor={String(field.name)} className="form-label">
                      {field.label}
                    </label>
                    {field.type === 'select' ? (
                      <Controller
                        name={field.name as Path<T>}
                        control={control}
                        defaultValue={formData[field.name as keyof T] || ''}
                        rules={{required: field.required}}
                        render={({field: controllerField}) => (
                          <select
                            {...controllerField}
                            className="form-control w-full !rounded-md"
                            disabled={isSubmitting}
                          >
                            <option value="">Select an option</option>
                            {field.selectOptions?.map(option => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        )}
                      />
                    ) : field.type === 'map' ? (
                      <Controller
                        name={field.name as Path<T>}
                        control={control}
                        defaultValue={formData[field.name as keyof T] || ''}
                        rules={{required: field.required}}
                        render={({field: controllerField}) => (
                          <Map
                            onChangeOverlays={overlays => controllerField.onChange(overlays)}
                            initialOverlays={formData[field.name as keyof T] || []}
                          />
                        )}
                      />
                    ) : (
                      <Controller
                        name={field.name as Path<T>}
                        control={control}
                        defaultValue={formData[field.name as keyof T] || ''}
                        rules={{required: field.required}}
                        render={({field: controllerField}) => (
                          <input
                            type={field.type}
                            className="form-control w-full !rounded-md"
                            placeholder={field.placeholder}
                            {...controllerField}
                            onChange={e => {
                              let formattedValue = e.target.value;
                              if (field.name === 'phone_number') {
                                formattedValue = formatPhoneNumber(e.target.value, 'US');
                                // Enforce maximum length
                                if (formattedValue.length <= MAX_PHONE_LENGTH) {
                                  controllerField.onChange(formattedValue);
                                }
                              } else {
                                controllerField.onChange(e);
                              }
                            }}
                            disabled={isSubmitting}
                          />
                        )}
                      />
                    )}
                    {errors[field.name as keyof T] && <span className="text-xs text-red">This field is required</span>}
                  </div>
                ))}
              </div>
            </div>
            <div className="ti-modal-footer flex flex-shrink-0 justify-between border-t p-4">
              <button
                type="button"
                className={`ti-btn ti-btn-light align-middle ${currentPage === 0 ? 'cursor-not-allowed opacity-50' : ''}`}
                onClick={handlePreviousPage}
                disabled={currentPage === 0 || isSubmitting}
              >
                Previous
              </button>
              {currentPage < totalPages - 1 && (
                <button
                  type="button"
                  className={`ti-btn ti-btn-light bg-primary align-middle ${currentPage === totalPages - 1 ? 'cursor-not-allowed opacity-50' : ''}`}
                  onClick={handleNextPage}
                  disabled={isSubmitting}
                >
                  Next
                </button>
              )}
              {currentPage === totalPages - 1 && (
                <button type="submit" className="ti-btn bg-primary !font-medium text-white" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
